import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";

import { Typography } from "@mui/material";

import {
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Bar,
  BarChart,
  Tooltip
} from "recharts";

import { Box, useTheme } from "@mui/material";

import { formatDecimal } from "@aclymatepackages/formatters";

import CustomChartBrush from "./CustomChartBrush";
import useChartWarningLabels from "./useChartWarningLabels";

import useDisplayMeasurementSystem from "../../../helpers/hooks/displayMeasurementSystem";

const ViewBarChart = ({
  data,
  setSelectedObject,
  yLabel = "Average Monthly Emissions",
  tooltipComponent,
  barAreasArray,
  dataType,
  showBrushAnnotation,
  unit,
  blurValues
}) => {
  const { palette, typography } = useTheme();
  const { carbonUnitLabel } = useDisplayMeasurementSystem();
  const params = useParams();
  const { view } = params || {};

  const { warningLabels, labelSetter } = useChartWarningLabels({
    data,
    warningField: "status",
    barSumField: "emissionsSumTons"
  });

  const calcInitialRange = useCallback(
    () =>
      data.length > 50 ? [data.length - 50, data.length] : [0, data.length],
    [data.length]
  );

  const initialRange =
    data.length > 50 ? [data.length - 50, data.length] : [0, data.length];

  const [range, setRange] = useState(calcInitialRange());
  const [startIndex, endIndex] = range;

  useEffect(() => {
    const newRange = calcInitialRange();
    setRange(newRange);
  }, [data.length, calcInitialRange]);

  const handleBarClick = (event) => {
    if (!event?.activePayload || event.activePayload.length === 0) {
      return;
    }

    const { activePayload } = event;

    if (dataType && dataType === "events") {
      return setSelectedObject(activePayload[0].payload.name);
    }

    return setSelectedObject(activePayload[0].payload.id);
  };

  const filteredData = data.slice(startIndex, endIndex + 1);

  const tonsPercentageSum = filteredData.reduce(
    (sum, { totalTonsCo2ePercentage }) => sum + totalTonsCo2ePercentage,
    0
  );
  const typePercentageSum = filteredData.reduce(
    (sum, { typeTonsCo2ePercentage }) => sum + typeTonsCo2ePercentage,
    0
  );

  const shouldShowBrush = data.length > 30;

  const BlurredTick = ({ x, y }) => (
    <text x={x} y={y} textAnchor="end" fill="#666">
      <tspan style={{ filter: "blur(4px)" }}>####</tspan>
    </text>
  );

  return (
    <Box position="relative">
      <ResponsiveContainer width="100%" aspect={4}>
        <BarChart
          width={500}
          height={300}
          data={filteredData}
          style={{ cursor: "pointer" }}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: shouldShowBrush ? 10 : 5
          }}
          onClick={(event) => handleBarClick(event)}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <YAxis
            width={70}
            name={yLabel}
            tick={blurValues ? <BlurredTick /> : true}
            style={{ fontFamily: typography.fontFamily }}
            tickFormatter={
              !blurValues &&
              ((value) => `${formatDecimal(value)} ${unit || carbonUnitLabel}`)
            }
          />
          <Tooltip wrapperStyle={{ zIndex: 99 }} content={tooltipComponent} />
          {barAreasArray.map(({ name, dataKey, color }, idx) => (
            <Bar
              key={`view-chart-bar-${idx}`}
              name={name}
              dataKey={dataKey}
              fill={color}
              stackId="a"
              isAnimationActive={false}
            >
              {labelSetter}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      {shouldShowBrush && (
        <>
          <CustomChartBrush
            min={0}
            max={data.length}
            onChange={setRange}
            initialRange={initialRange}
            step={1}
            brushColor={palette[view]?.main || palette.primary.main}
          />
          {showBrushAnnotation && (
            <Typography variant="caption" align="center" display="block">
              {range[1] - range[0]} {dataType} |{" "}
              {formatDecimal(typePercentageSum)}% of {dataType} emissions |{" "}
              {formatDecimal(tonsPercentageSum)}% of total emissions
            </Typography>
          )}
        </>
      )}
      {warningLabels}
    </Box>
  );
};
export default ViewBarChart;
